import React, { createContext, useContext, useEffect } from "react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import PropTypes from "prop-types";
import { FormContext } from ".";
import type { FormContextValue, FormValidator } from "./types";
import useData from "./useData";

const getInputField = (inputField: ReactElement<InputProps>) => inputField.props.field;
const filterChildrenForInputFields = (children: ReactNode) =>
  (React.Children.toArray(children) as ReactElement<InputProps>[]).filter(
    (child) => child.props.field !== undefined
  );
const mapChildrenToInputFieldNames = (children: ReactNode) =>
  filterChildrenForInputFields(children).map(getInputField);

const FieldContext = createContext<FieldContextValue | undefined>(undefined);
const FieldContextProvider = ({
  children,
  field,
  dispatchMetadata: parentDispatchMetadata,
  dispatchData: dispatchParentData,
}: FieldContextProviderProps) => {
  const formContext = useContext(FormContext)!;
  const fieldContext = useContext(FieldContext);

  const context =
    fieldContext && fieldContext.formID === formContext.formID ? fieldContext : formContext;

  const { dispatchData: dispatchMetadata } = useData({
    field,
    dispatchData: parentDispatchMetadata,
  });
  const { dispatchData } = useData({
    field,
    dispatchData: dispatchParentData,
    initialData: context.data?.[field as Field] || {},
  });

  useEffect(() => {
    dispatchData({
      type: "update",
      update: context.data?.[field as Field] || {},
    });
  }, [context.data?.[field as Field]]);

  return (
    <FieldContext.Provider
      value={{
        formID: formContext.formID,
        dispatchMetadata,
        dispatchData,
        data: context.data?.[field as Field],
        metadata: context.metadata?.[field as Field] as FormContextValue["metadata"],
      }}
    >
      {children}
    </FieldContext.Provider>
  );
};

const ContextFormField = ({
  children,
  field: wrapperField,
  style,
  validate,
  required,
  testId,
  className,
}: ContextFormFieldProps) => {
  const field = wrapperField || mapChildrenToInputFieldNames(children)[0];
  const formContext = useContext(FormContext)!;
  const fieldContext = useContext(FieldContext);
  const context =
    fieldContext && fieldContext.formID === formContext.formID ? fieldContext : formContext;

  // on first render, update form metadata with validator and required.
  useEffect(() => {
    if (wrapperField) return;
    if (context === fieldContext) {
      fieldContext!.dispatchMetadata({
        type: "updateField",
        field,
        update: { validate, required },
      });
    } else {
      if (!Object.prototype.hasOwnProperty.call(formContext.metadata, field as Field)) {
        formContext.dispatchMetadata({
          type: "updateField",
          field,
          update: { validate, required },
        });
      }
    }
  }, [formContext.data, formContext.metadata]);

  // remove the field from the metadata context when the component unmounts
  useEffect(() => {
    return () => {
      context.dispatchMetadata({
        type: "deleteField",
        field: field as Field,
      });
    };
  }, []);

  return (
    <FieldContextProvider
      field={wrapperField}
      dispatchMetadata={context.dispatchMetadata}
      dispatchData={context.dispatchData}
    >
      <div className={`context-form-field ${className}`} style={style} data-testid={testId}>
        {React.Children.map(children as ReactElement<InputProps>[], (child) => {
          if (wrapperField) return child;
          if (!child) return child;
          if (child.props.field === undefined) return child;
          if (field !== child.props.field) return child;
          return React.cloneElement(child, {
            onChange: (event: unknown) => {
              if (child.props && child.props.onChange) {
                child.props.onChange(event);
              }
              if (event && (event instanceof Event || (event as ChangeEvent).nativeEvent)) {
                if ((event as ChangeEvent).target.type === "checkbox") {
                  context.dispatchData({
                    type: "updateField",
                    field,
                    update: (event as ChangeEvent).target.checked,
                  });
                  return;
                }
                context.dispatchData({
                  type: "updateField",
                  field,
                  update: (event as ChangeEvent).target.value,
                });
                return;
              }
              context.dispatchData({
                type: "updateField",
                field,
                update: event,
              });
            },
            value: context.data?.[field as Field] || "",
            data: context.data?.[field as Field] || "",
            checked: context.data?.[field as Field] || "",
            error: context.metadata?.[field as Field]?.error || "",
            errorText: context.metadata?.[field as Field]?.error || "",
            required,
          });
        })}
      </div>
    </FieldContextProvider>
  );
};

ContextFormField.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  validate: PropTypes.func,
  required: PropTypes.bool,
  className: PropTypes.string,
};

type Field = string | number;
type DispatchData = ReturnType<typeof useData>["dispatchData"];
type InputProps = {
  field?: Field;
  onChange?: (value: unknown) => void;
  value?: unknown;
  data?: unknown;
  checked?: unknown;
  error?: unknown;
  errorText?: unknown;
  required?: boolean;
};
type FieldContextValue = Pick<FormContextValue, "formID" | "dispatchMetadata" | "dispatchData"> & {
  data?: FormContextValue["data"];
  metadata?: FormContextValue["metadata"];
};
type FieldContextProviderProps = {
  children?: ReactNode;
  field?: Field;
  dispatchMetadata: DispatchData;
  dispatchData: DispatchData;
};
type ContextFormFieldProps = {
  children?: ReactNode;
  field?: Field;
  style?: CSSProperties;
  validate?: FormValidator;
  required?: boolean;
  testId?: string;
  className?: string;
};
type ChangeEvent = {
  nativeEvent?: unknown;
  target: { type?: string; checked?: boolean; value?: unknown };
};

export default ContextFormField;
